import { useCallback, useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Row,
  Col,
  Image,
  message,
  Switch,
} from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import UserModal from "./UserModal";
import blink from "../../assets/images/blink.png";
import { User } from "../../types/user";
import { ListResponse, Pagination } from "../../types";
import { userService } from "../../services";

const { Text } = Typography;

const UsersManagement = () => {
  const columns = [
    {
      title: "Tên",
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: "Họ",
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: "Vai trò",
      dataIndex: "role",
      key: "role",
      render: (role: string) => {
        if (role === "admin")
          return (
            <Text style={{ color: "#1890ff", fontWeight: 500 }}>
              {role.charAt(0).toUpperCase() + role.slice(1)}
            </Text>
          );
        if (role === "hr")
          return (
            <Text style={{ color: "#52c41a", fontWeight: 500 }}>
              {role.toUpperCase()}
            </Text>
          );
        return <Text>{role}</Text>;
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      render: (_: any, record: User) => (
        <Switch
          style={{ width: 100 }}
          checkedChildren="Hoạt động"
          unCheckedChildren="Không hoạt động"
          checked={record.status === "active"}
          onChange={(checked) => {
            setEditingRecord(record);
            setIsStatusModalVisible(true);
          }}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: User) => (
        <Space>
          <Button
            icon={<EditOutlined style={{ paddingLeft: "4px" }} />}
            onClick={() => {
              showUserModal(record);
            }}
          />
        </Space>
      ),
    },
  ];

  const [form] = Form.useForm();
  const [isUserModalVisible, setIsUserModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<User | null>(null);
  const [usersData, setUsersData] = useState<ListResponse<User>>();
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersPagination, setUsersPagination] = useState<Pagination>({
    page: 1,
    resultsPerPage: 10,
  });
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);

  const getUserList = useCallback(async () => {
    try {
      setIsLoadingUsers(true);
      const response = await userService.getUserList(usersPagination);
      setUsersData(response);
    } catch (error) {
      message.error("Lỗi khi lấy danh sách người dùng: " + error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [usersPagination]);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  const handlePaginationChange = (page: number, pageSize: number) => {
    setUsersPagination({
      ...usersPagination,
      page,
      resultsPerPage: pageSize,
    });
  };

  const handleUsersSearch = (value: string) => {
    setUsersPagination({
      ...usersPagination,
      page: 1,
      search: value,
    });
  };

  const showUserModal = (record: User | null = null) => {
    if (record) {
      form.setFieldsValue({
        ...record,
      });
      setEditingRecord(record);
    } else {
      form.resetFields();
      setEditingRecord(null);
    }
    setIsUserModalVisible(true);
  };

  const handleCancelUserModal = () => {
    setIsUserModalVisible(false);
    form.resetFields();
    setEditingRecord(null);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingRecord) {
        await userService.updateUser(editingRecord.id, values);
        message.success("Cập nhật người dùng thành công");
      } else {
        await userService.createUser(values);
        message.success("Thêm người dùng thành công");
      }
      handleCancelUserModal();
      await getUserList();
    } catch (error) {
      message.error("Lỗi khi thêm người dùng: " + error);
    }
  };

  const confirmStatusChange = async () => {
    try {
      if (!editingRecord?.id) return;
      await userService.updateUserStatus(editingRecord?.id, {
        newStatus: editingRecord?.status === "active" ? "inactive" : "active",
      });
      message.success("Thay đổi trạng thái người dùng thành công");
      await getUserList();
    } catch (error) {
      message.error("Lỗi khi thay đổi trạng thái người dùng: " + error);
    } finally {
      setIsStatusModalVisible(false);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <h1 className="page-title">
        Quản lý người dùng <Image src={blink} preview={false} className="blink-icon" />
      </h1>
      <Row
        style={{
          marginBottom: 16,
        }}
      >
        <Col xs={24} sm={15} md={12} lg={20} xl={20} style={{ marginTop: 10 }}>
          <Input.Search
            placeholder="Tìm kiếm"
            onChange={(e) => handleUsersSearch(e.target.value)}
            style={{ maxWidth: 500 }}
            className="user-search-input"
          />
        </Col>
        <Col
          xs={24}
          sm={9}
          md={12}
          lg={4}
          xl={4}
          style={{ textAlign: "right", marginTop: 10 }}
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showUserModal()}
          >
            Thêm
          </Button>
        </Col>
      </Row>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={usersData ? usersData.data : []}
        pagination={{
          current: usersPagination.page,
          pageSize: usersPagination.resultsPerPage,
          total: usersData ? usersData.totalResults : 0,
          onChange: handlePaginationChange,
          showQuickJumper: true,
        }}
        loading={isLoadingUsers}
        scroll={{ x: "max-content" }}
      />

      <UserModal
        visible={isUserModalVisible}
        form={form}
        onFinish={handleSubmit}
        onCancel={handleCancelUserModal}
        editingRecord={editingRecord}
      />

      <Modal
        title="Thay đổi trạng thái người dùng"
        open={isStatusModalVisible}
        onOk={confirmStatusChange}
        onCancel={() => setIsStatusModalVisible(false)}
        okText="Lưu"
        okButtonProps={{ danger: true }}
        cancelText="Huỷ"
      >
        Bạn có chắc chắn muốn đổi trạng thái của người dùng{" "}
        {editingRecord?.firstName} {editingRecord?.lastName} thành{" "}
        {editingRecord?.status === "active" ? "Không hoạt động" : "Hoạt động"}{" "}
        không?
      </Modal>
    </div>
  );
};

export default UsersManagement;
